import { CHECK_STATUSES } from '../../../constants/plateChecks';
import { resolveLinkedRecordNav } from '../../../utils/linkedRecordNav';
import { displayName } from '../../../utils/userLabel';
import { conditionsSummary } from '../../PlateConditionsFields';
import { canDo } from '../stages';
import { TERMINAL_PLATE, plateHasSlants, plateableOrigin } from '../quickActions';
import { Code, Day, Joined, Ltr, PlatePill } from '../cells';
import * as S from '../../specimens/styles';

/*
 * Lab notes: the isolate's plates, each with its own buttons and a status
 * select for a one-tap growth update, then every note written about it --
 * on the specimen, its stages, its collection or intake, plates, checks,
 * slants, reviews, extracts, runs and identifications -- newest first, each
 * linked to the record it was written on.
 *
 * A made plate with no slant set yet offers "Deposit to bank": its set cut
 * and stored in one step, ready or not -- the form says when it is not
 * marked ready to transfer.
 */
export default function LabNotesTab({ item, dossier, user, t, onAction, onNavigate }) {
  const plates = dossier.plates || [];
  const when = (c) => String(c.check_date || c.date || '');
  const checksOf = (plateId) => (dossier.checks || []).filter((c) => c.plate_id === plateId)
    .sort((a, b) => when(b).localeCompare(when(a)));
  const notes = [...(dossier.notes || [])].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
  const canCheck = canDo(user, 'logCheck', item);
  const canDeposit = canDo(user, 'deposit', item);

  const go = (table, id) => {
    const nav = resolveLinkedRecordNav(table, id);
    if (nav) onNavigate(nav.path, nav.state);
  };

  return (
    <div>
      <div className="hub-tab-actions">
        {canDo(user, 'startPlate', item) && !plates.length && plateableOrigin(item) && (
          <button type="button" style={S.ghostBtn} onClick={() => onAction('startPlate', item)}
            title={t('tip.action.startPlate')}>{t('action.startPlate')}</button>
        )}
        {canDo(user, 'activate', item) && item.sample_id && plates.some((p) => p.status === 'awaiting_creation') && (
          <button type="button" style={S.ghostBtn} onClick={() => onAction('activate', item)}
            title={t('tip.action.activate')}>{t('action.activate')}</button>
        )}
      </div>

      {plates.length === 0 && <p style={S.hint}>{t('details.noPlates')}</p>}
      <ul className="hub-records">
        {plates.map((p) => {
          const live = !TERMINAL_PLATE.includes(p.status) && p.status !== 'awaiting_creation';
          const cut = plateHasSlants(p, dossier.slants || []);
          const last = checksOf(p.plate_id)[0] || p.last_check;
          return (
            <li key={p.plate_id} className="hub-record" data-testid={`hub-plate-${p.plate_id}`}>
              <div className="hub-record__head">
                <button type="button" className="hub-link" onClick={() => go('plate_culture', p.plate_id)}
                  title={t('tip.openRecord')}><Code strong>{p.plate_code}</Code></button>
                <PlatePill status={p.status} t={t} />
                <span style={S.hint}>
                  {[p.protocol, p.temperature_c != null ? t('lab.target', { temp: p.temperature_c }) : '',
                    p.days_in_culture != null ? t('lab.days', { count: p.days_in_culture }) : ''].filter(Boolean).join(' · ')}
                </span>
              </div>
              {last && (last.date || last.check_date) && (
                <div style={S.hint}>
                  {t('lab.lastCheck')}: <Joined parts={[
                    <Day key="day" value={last.date || last.check_date} />,
                    last.colony_diameter_mm != null ? t('lab.colony', { mm: last.colony_diameter_mm }) : null,
                    last.sporulation_stage ? conditionsSummary({ sporulation_stage: last.sporulation_stage }, t) : null,
                    // Kept left to right, so it does not read "C° 22.5" right to left.
                    last.incubation_temp_c != null ? <Ltr key="temp">{`${last.incubation_temp_c} °C`}</Ltr> : null,
                    last.light_regime ? conditionsSummary({ light_regime: last.light_regime }, t) : null,
                  ]} />
                </div>
              )}
              {live && (canCheck || canDeposit) && (
                <div className="hub-record__actions">
                  {canCheck && (
                    <>
                      <label style={{ ...S.hint, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {t('lab.growthUpdate')}
                        <select value="" style={{ ...S.input, padding: '3px 6px', fontSize: 13 }}
                          title={t('tip.growthUpdate')} data-testid={`hub-plate-status-${p.plate_id}`}
                          onChange={(e) => e.target.value && onAction('quickStatus', item, { plateId: p.plate_id, status: e.target.value })}>
                          <option value="">{t('lab.chooseStatus')}</option>
                          {CHECK_STATUSES.map((s) => <option key={s} value={s}>{t(`plates:plateStatus.${s}`)}</option>)}
                        </select>
                      </label>
                      {['logCheck', 'conditions', 'labPhoto'].map((key) => (
                        <button key={key} type="button" style={smallBtn} onClick={() => onAction(key, item, { plateId: p.plate_id })}
                          title={t(`tip.action.${key}`)} data-testid={`hub-plate-${key}-${p.plate_id}`}>{t(`action.${key}`)}</button>
                      ))}
                    </>
                  )}
                  {p.status === 'ready_to_transfer' && !cut && canDo(user, 'cutSlants', item) && (
                    <button type="button" style={smallBtn} onClick={() => onAction('cutSlants', item, { plateId: p.plate_id })}
                      title={t('tip.action.cutSlants')}>{t('action.cutSlants')}</button>
                  )}
                  {!cut && canDeposit && (
                    <button type="button" style={smallBtn} onClick={() => onAction('deposit', item, { plateId: p.plate_id })}
                      title={t('tip.action.deposit')} data-testid={`hub-plate-deposit-${p.plate_id}`}>{t('action.deposit')}</button>
                  )}
                  {!p.has_dna && canDo(user, 'extractDna', item) && (
                    <button type="button" style={smallBtn} onClick={() => onAction('extractDna', item, { source: `plate:${p.plate_id}` })}
                      title={t('tip.action.extractDna')}>{t('action.extractDna')}</button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <h4 style={S.sectionTitle}>{t('details.notes')}</h4>
      {notes.length === 0 && <p style={S.hint}>{t('details.noNotes')}</p>}
      <ol className="hub-timeline" data-testid="hub-notes">
        {notes.map((n, i) => (
          <li key={`${n.record_table}-${n.record_id}-${i}`}>
            <div style={S.hint}>
              <Day value={n.at} /> · {t(`notes.source.${n.source}`, { defaultValue: n.source })}
              {n.record_code && (
                <> · {resolveLinkedRecordNav(n.record_table, n.record_id) ? (
                  <button type="button" className="hub-link" onClick={() => go(n.record_table, n.record_id)}
                    title={t('tip.openRecord')}><Code>{n.record_code}</Code></button>
                ) : <Code>{n.record_code}</Code>}</>
              )}
              {n.by_name && <> · {displayName(n.by_name, t)}</>}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.text}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

const smallBtn = { ...S.ghostBtn, padding: '4px 10px', fontSize: 13 };
